import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import { coerceSchema, renderSubmissionHtml, FUSION_LOCKUP } from '../lib/hostedForm';
import {
  sectionsOf, sectionState, outstandingToSign, outstandingMessage,
  readingProgress, resumeSectionId, carryProgressToVersion,
  VISITED, CURRENT,
} from '../lib/longDocument';
import { formatDateBritish, todayIso } from '../lib/compliance';
import { supplierIdentity } from '../lib/supplierIdentity';
import { verifyMetaBeforeSigning, contentHashForMeta } from '../lib/documentVersion';
import { FormBlock } from '../components/compliance/HostedFormBlocks';
import SignaturePad from '../components/compliance/SignaturePad';

// Full-page hosted-document signer.
//
// THE MODAL IS GONE (long-documents §6). One renderer serves a one-paragraph
// acknowledgement and a ten-page code of conduct — a dialog was never the right
// container for a document, and it was the reason "the forms aren't displaying
// correctly" on anything longer than a screen.
//
// SECTIONS, NOT PAGES (§2). Pages exist at print time only. Sections come from
// the block list, so a short document is one section and behaves exactly as it
// did before.
//
// THE GATE (§4) is "every section opened", and nothing more. No scroll depth,
// no dwell minimum, no timed Continue button — each is defeated by scrolling to
// the bottom and waiting, and each is punishing on a phone. Dwell IS recorded
// as evidence and never enforced. The Sign control always NAMES what's
// outstanding rather than sitting there disabled saying nothing.

const BUCKET = 'supplier-uploads';

const CheckIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2F6E14" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const Spinner = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" className="animate-spin">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" fill="none" strokeOpacity="0.25" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" />
  </svg>
);

// `delegated` — set when a colleague is completing this via /d/<token> rather
// than the supplier themselves. Two effects, and only two:
//   * scope 'complete' means they DON'T sign; the signature block is replaced
//     with "this goes back to {inviter}", per the brief.
//   * the write goes through the caller's onSubmit (a service-role endpoint),
//     because a delegate holds no session and so can't write to Supabase.
// Everything else — the sectioned reading, the gate, the clause ticks — is
// identical on purpose. A delegated document must not be a second, quietly
// diverging implementation of reading and signing.
export default function HostedFormSignPage({ document: docMeta, supplier, supplierId, signer, onSuccess, onClose, delegated = null }) {
  const schema = useMemo(() => coerceSchema(docMeta?.formSchema), [docMeta?.formSchema]);
  const sections = useMemo(() => sectionsOf(schema), [schema]);
  const todayLabel = useMemo(() => formatDateBritish(todayIso()), []);
  const version = docMeta?.versionValue ?? null;

  // Read through the shared helper — the columns are `supplier_reg_number`,
  // `supplier_vat_number` and five separate address columns, and reading
  // `reg_number`/`vat_number`/`address` (as this did) silently prefilled
  // nothing, so a supplier couldn't tell an empty field from one we never had.
  const identityDefaults = useMemo(
    () => supplierIdentity(supplier, { todayLabel }),
    [supplier, todayLabel],
  );

  const [values, setValues] = useState(identityDefaults);
  const [edited, setEdited] = useState(false);
  const [ticks, setTicks] = useState({});
  const [signerName, setSignerName] = useState(signer?.name || '');
  const [signatureImage, setSignatureImage] = useState(null);
  const [state, setState] = useState('input'); // input | submitting | success | error
  const [errorMessage, setErrorMessage] = useState(null);
  const [saveNote, setSaveNote] = useState(null);

  const [visited, setVisited] = useState([]);
  const [currentId, setCurrentId] = useState(sections[0]?.id || null);
  const [progressLoaded, setProgressLoaded] = useState(false);

  const contentRef = useRef(null);
  const events = useRef([]);          // reading evidence, never a gate
  const sectionEnteredAt = useRef(Date.now());
  const userIdRef = useRef(null);

  const submitting = state === 'submitting';
  const currentIndex = Math.max(0, sections.findIndex(s => s.id === currentId));
  const current = sections[currentIndex] || sections[0];
  const outstanding = outstandingToSign(sections, { visited, ticks });
  const sigIndex = sections.findIndex(s => s.hasSignature);

  // ── Load saved progress (§5 resume) ───────────────────────────────────────
  // Any read failure is non-fatal: the document must still be readable if the
  // progress table is unavailable, so we fall back to starting at the top
  // rather than blocking on it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        userIdRef.current = user?.id || null;
        if (!user?.id || !docMeta?.key) { if (!cancelled) setProgressLoaded(true); return; }
        const { data, error } = await supabase
          .from('document_reading_progress')
          .select('visited_sections, last_section_id, document_version')
          .eq('user_id', user.id)
          .eq('document_key', docMeta.key)
          .maybeSingle();
        if (cancelled) return;
        if (!error && data) {
          // A new version resets everything — see carryProgressToVersion for
          // why that's the conservative choice.
          const carried = carryProgressToVersion(
            sections,
            { visited: data.visited_sections || [], lastSectionId: data.last_section_id },
            { sameVersion: (data.document_version ?? null) === version },
          );
          setVisited(carried.visited);
          setCurrentId(resumeSectionId(sections, carried));
        }
      } catch { /* progress is a convenience, never a blocker */ }
      if (!cancelled) setProgressLoaded(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docMeta?.key, version]);

  // Mark the current section visited as soon as it's shown.
  useEffect(() => {
    if (!currentId || !progressLoaded) return;
    setVisited(prev => (prev.includes(currentId) ? prev : [...prev, currentId]));
  }, [currentId, progressLoaded]);

  const persist = useCallback(async (nextVisited, nextCurrent, { quiet = true } = {}) => {
    const uid = userIdRef.current;
    if (!uid || !supplierId || !docMeta?.key) return false;
    const row = {
      supplier_id: supplierId,
      user_id: uid,
      document_key: docMeta.key,
      document_version: version,
      visited_sections: nextVisited,
      last_section_id: nextCurrent,
      events: events.current.slice(-200), // evidence, capped so a long read can't bloat the row
      updated_at: new Date().toISOString(),
    };
    const { error } = await supabase
      .from('document_reading_progress')
      .upsert(row, { onConflict: 'user_id,document_key,document_version' });
    // A failed save must not be silent — "Save and finish later" that didn't
    // save is exactly the confirmation-that-lies pattern.
    if (error && !quiet) setSaveNote(`Couldn't save your place — ${error.message}`);
    return !error;
  }, [supplierId, docMeta?.key, version]);

  // Record dwell for the section being left, then move.
  const goToSection = useCallback((id) => {
    if (!id || id === currentId) return;
    const now = Date.now();
    events.current.push({ sectionId: currentId, at: new Date(sectionEnteredAt.current).toISOString(), dwellMs: now - sectionEnteredAt.current });
    sectionEnteredAt.current = now;
    const nextVisited = visited.includes(id) ? visited : [...visited, id];
    setVisited(nextVisited);
    setCurrentId(id);
    setSaveNote(null);
    // Scrolling to the top of the CONTENT, not the window: the reader lands at
    // the start of the section they just opened, which is the whole point of
    // splitting it up.
    if (contentRef.current) contentRef.current.scrollIntoView({ block: 'start', behavior: 'auto' });
    persist(nextVisited, id);
  }, [currentId, visited, persist]);

  const setFieldValue = useCallback((source, v) => {
    setValues(prev => ({ ...prev, [source]: v }));
    if (v !== identityDefaults[source]) setEdited(true);
  }, [identityDefaults]);
  const setInputValue = useCallback((id, v) => setValues(prev => ({ ...prev, [id]: v })), []);
  const setTick = useCallback((id, on) => setTicks(prev => ({ ...prev, [id]: on })), []);

  // Attachment answers (Rob, 2026-08-14 — "areas within form building for
  // suppliers to upload attachments in response to questions").
  //
  // Uploads to the SAME supplier-uploads bucket every other supplier document
  // uses, under the form's own folder. A second bucket would be a second set of
  // policies to keep in step, and this is the same class of thing: a file a
  // supplier has given us in support of a compliance answer.
  //
  // The value stored against the token is { url, name } — the answer to that
  // question is the document, so it travels with the rest of the answers into
  // the stored copy rather than living in a separate attachments table.
  const [uploadingId, setUploadingId] = useState(null);
  const setAttachment = useCallback(async (id, file) => {
    if (!file) { setValues(prev => ({ ...prev, [id]: null })); return; }
    // 15MB. Above that it is not an answer to a question, it is a data
    // transfer, and the signing page is the wrong place for it.
    if (file.size > 15 * 1024 * 1024) {
      setErrorMessage(`${file.name} is ${(file.size / 1024 / 1024).toFixed(1)}MB — attachments are capped at 15MB. Send larger files to your Fusion contact directly.`);
      return;
    }
    setUploadingId(id);
    setErrorMessage(null);
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `hosted-form-attachments/${docMeta?.key || 'form'}/${id}-${safeName}`;
    const { error: upErr } = await supabase.storage
      .from(BUCKET)
      .upload(path, file, { contentType: file.type, upsert: true });
    if (upErr) {
      setUploadingId(null);
      setErrorMessage(`Could not attach ${file.name}: ${upErr.message}`);
      return;
    }
    const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(path);
    setValues(prev => ({ ...prev, [id]: { url: pub?.publicUrl || null, name: file.name } }));
    setUploadingId(null);
  }, [docMeta?.key]);

  // ── What still blocks a signature ─────────────────────────────────────────
  const inputTokens = useMemo(() => {
    const out = [];
    schema.blocks.forEach(b => {
      if (b.type === 'paragraph') (b.lines || []).forEach(ln => (ln.tokens || []).forEach(t => { if (t.t === 'input') out.push(t); }));
    });
    return out;
  }, [schema]);
  // An attachment answer is a { url, name } object, not a string — the string
  // test happened to work (an object stringifies to something truthy) but only
  // by accident, and an accident is a poor guard on "may this be signed".
  const requiredInputsFilled = inputTokens.filter(t => !t.optional).every(t => (
    t.ftype === 'attachment'
      ? Boolean(values[t.id]?.url)
      : Boolean(String(values[t.id] || '').trim())
  ));
  const signatureFillExtras = useMemo(() => {
    const sig = schema.blocks.find(b => b.type === 'signature');
    return (sig?.extras || []).filter(e => !e.source && String(e.label || '').trim());
  }, [schema]);
  const signatureExtrasFilled = signatureFillExtras.every(e => String(values[e.id] || '').trim());
  const readingMsg = outstandingMessage(sections, { visited, ticks });

  // A 'complete' delegate fills the document in but does NOT sign it — so
  // asking them for a name and a signature would be asking for something they
  // have no authority to give.
  const signsHere = !delegated || delegated.scope === 'complete_and_sign';

  const canSubmit = !readingMsg && requiredInputsFilled
    && (!signsHere || (signatureExtrasFilled && signerName.trim().length > 0 && !!signatureImage));

  const blockingMessage = readingMsg
    ? readingMsg
    : !requiredInputsFilled ? 'Some required fields are still empty'
      : !signsHere ? null
        : !signatureExtrasFilled ? 'The signature details are incomplete'
          : !signerName.trim() ? 'Your full name is needed'
            : !signatureImage ? 'Your signature is needed'
              : null;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !supplierId || !docMeta?.key) return;
    setState('submitting'); setErrorMessage(null);

    // A delegate has no session, so the write happens server-side against
    // their token. Everything collected here is handed over intact; the
    // endpoint re-checks every gate before it commits anything.
    if (delegated?.onSubmit) {
      try {
        events.current.push({ sectionId: currentId, at: new Date(sectionEnteredAt.current).toISOString(), dwellMs: Date.now() - sectionEnteredAt.current });
        await delegated.onSubmit({
          values, ticks, signerName: signerName.trim(), signatureImage,
          reading: { sections: sections.map(s => s.id), visited, events: events.current.slice(-200) },
        });
      } catch (e) {
        setErrorMessage(e.message || 'Could not submit. Try again.');
        setState('error');
      }
      return;
    }

    try {
      const evidence = verifyMetaBeforeSigning(docMeta);
      if (evidence.state === 'mismatch') {
        throw new Error("This document has changed since it was published, so it can't be signed right now. Please tell Fusion 2K — they'll publish it properly as a new version.");
      }
      const signedAt = new Date().toISOString();
      const html = renderSubmissionHtml(schema, {
        fields: values, ticks, signerName: signerName.trim(), signedAtLabel: todayLabel,
        docLabel: docMeta.label, version, signatureImage,
      });
      const blob = new Blob([html], { type: 'text/html' });
      const path = `${supplierId}/${docMeta.key}-form-${Date.now()}.html`;
      const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, blob, { contentType: 'text/html', upsert: true });
      if (upErr) throw new Error(upErr.message);
      const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(path);
      const htmlUrl = pub?.publicUrl || null;

      // Close the final section's dwell so the evidence covers the whole read.
      events.current.push({ sectionId: currentId, at: new Date(sectionEnteredAt.current).toISOString(), dwellMs: Date.now() - sectionEnteredAt.current });

      const { error: subErr } = await supabase.from('hosted_form_submissions').insert({
        supplier_id: supplierId,
        document_key: docMeta.key,
        document_id: docMeta.documentId || null,
        document_version: version,
        responses: {
          fields: values, clauses: ticks, edited_identity: edited,
          // How it was read, alongside what was agreed (§4 evidence).
          reading: { sections: sections.map(s => s.id), visited, events: events.current.slice(-200) },
        },
        signer_name: signerName.trim(),
        signer_email: signer?.email || null,
        signed_at: signedAt,
        document_html_url: htmlUrl,
        created_by: userIdRef.current,
        content_hash: evidence.actual || contentHashForMeta(docMeta),
      });
      if (subErr) throw new Error(subErr.message);

      const { error: sodErr } = await supabase.from('supplier_onboarding_documents').upsert({
        supplier_id: supplierId,
        document_key: docMeta.key,
        supplier_uploaded_file_url: htmlUrl,
        supplier_uploaded_file_name: `${docMeta.label} — completed ${todayLabel}`,
        supplier_uploaded_at: signedAt,
        expiry_date: null, expiry_approved_at: null, expiry_approved_by: null,
        signed_at: null, review_decision: null, review_reason: null, review_notes: null,
        reviewed_at: null, reviewed_by: null, supplier_reply: null, supplier_reply_at: null,
        updated_at: signedAt,
      }, { onConflict: 'supplier_id,document_key' });
      if (sodErr) throw new Error(sodErr.message);

      setState('success');
      setTimeout(() => onSuccess?.(), 1600);
    } catch (e) {
      setErrorMessage(e.message || 'Could not submit. Try again.');
      setState('error');
    }
  }, [canSubmit, supplierId, docMeta, schema, values, ticks, signerName, signatureImage, edited, signer, todayLabel, version, sections, visited, currentId, onSuccess, delegated]);

  if (state === 'success') {
    return (
      <div className="fixed inset-0 z-[70] bg-canvas flex items-center justify-center p-6">
        <div className="w-full max-w-[460px] bg-white border border-line rounded-2xl px-7 py-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4" style={{ backgroundColor: 'rgba(47,110,20,0.1)', border: '1.5px solid rgba(47,110,20,0.4)' }}>
            <CheckIcon />
          </div>
          <h2 className="font-['Metropolis'] text-lg font-extrabold text-ink mb-2">Signed and submitted</h2>
          <p className="font-['Metropolis'] text-[12px] text-fusion-mid-grey leading-relaxed max-w-[320px] mx-auto">
            Fusion 2K will review your completed {docMeta.label} and let you know once it's approved.
          </p>
        </div>
      </div>
    );
  }

  const pct = Math.round(readingProgress(sections, visited) * 100);
  const multi = sections.length > 1;

  return (
    // Full-page takeover, matching PdfSignPage — a document is not a dialog.
    // The scroll lives here, so the header's `sticky top-0` sticks to this
    // container rather than the page behind it.
    <div className="fixed inset-0 z-[70] bg-canvas font-['Metropolis'] overflow-y-auto">
      {/* ── Header: what this is, how far through, and a way out ──────────── */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-line">
        <div className="max-w-[1180px] mx-auto px-6 py-3">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h1 className="text-lg font-extrabold text-ink leading-tight truncate">{docMeta.label}</h1>
              <p className="text-[11px] text-[#51534A]">
                {version ? `Version ${version} · ` : ''}
                {multi ? `Section ${currentIndex + 1} of ${sections.length}` : 'Read and sign'}
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {/* Not offered to a delegate: progress is stored per signed-in
                  user and they have no session, so the button could only ever
                  pretend to have saved. They get a plain way out instead. */}
              <button
                type="button"
                disabled={submitting}
                onClick={async () => {
                  if (delegated) { onClose?.(); return; }
                  const ok = await persist(visited, currentId, { quiet: false });
                  if (ok) { setSaveNote('Saved — you can come back to this later.'); onClose?.(); }
                }}
                className="rounded-xl bg-white border border-line text-ink text-sm font-semibold px-4 py-2 transition-colors hover:border-[#C9CFB6] hover:bg-[#FAFBF4] disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest">
                {delegated ? 'Close' : 'Save and finish later'}
              </button>
            </div>
          </div>
          {multi && (
            <div className="mt-2.5">
              <div className="h-1.5 rounded-full bg-[#EEF1E6] overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Sections opened">
                <div className="h-full bg-[#92D400] transition-[width] duration-300" style={{ width: `${pct}%` }} />
              </div>
              <p className="text-[10px] text-[#51534A] mt-1">{pct}% — {visited.length} of {sections.length} sections opened</p>
            </div>
          )}
        </div>
      </div>

      {saveNote && (
        <div className="max-w-[1180px] mx-auto px-6 pt-3">
          <p role="status" className="text-[13px] text-fusion-forest">{saveNote}</p>
        </div>
      )}

      <div className={`max-w-[1180px] mx-auto px-6 py-6 grid grid-cols-1 gap-6 ${multi ? 'lg:grid-cols-[260px_1fr]' : ''}`}>

        {/* ── Contents (§3) ─────────────────────────────────────────────────
            Every section, its state, and — where a section still has clauses
            to agree — HOW MANY. That count is the point: without it a supplier
            with one unticked clause in an eight-section document has no way to
            find the thing blocking their signature. */}
        {multi && (
          <nav aria-label="Document contents" className="lg:sticky lg:top-[104px] lg:self-start">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#9BA08C] mb-2">Contents</p>
            <ol className="space-y-1">
              {sections.map((s, i) => {
                const st = sectionState(s, currentId, visited);
                const outstandingHere = s.clauseIds.filter(id => !ticks[id]).length;
                const locked = s.hasSignature && outstanding.signatureLocked;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => !locked && goToSection(s.id)}
                      disabled={locked}
                      aria-current={st === CURRENT ? 'step' : undefined}
                      aria-disabled={locked || undefined}
                      title={locked ? `Open the remaining ${outstanding.unopenedCount} section${outstanding.unopenedCount === 1 ? '' : 's'} first` : undefined}
                      className={`w-full text-left rounded-lg px-2.5 py-2 flex items-start gap-2 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest ${
                        st === CURRENT ? 'bg-fusion-forest text-white'
                          : locked ? 'text-[#9BA08C] cursor-not-allowed'
                            : 'text-[#51534A] hover:bg-[#EEF1E6] hover:text-ink'
                      }`}>
                      {/* State is carried by a WORD as well as the mark — a tick
                          glyph or a colour alone doesn't survive greyscale or a
                          screen reader. */}
                      <span className="text-[11px] tabular-nums flex-shrink-0 mt-px w-4">
                        {st === VISITED ? '✓' : locked ? '🔒' : i + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-semibold leading-snug">{s.title}</span>
                        <span className={`block text-[10px] ${st === CURRENT ? 'text-white/75' : 'text-[#9BA08C]'}`}>
                          {locked ? 'Locked until you\'ve opened every section'
                            : outstandingHere > 0 ? `${outstandingHere} to agree`
                              : st === VISITED ? 'Read'
                                : st === CURRENT ? 'Reading now' : 'Not opened yet'}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </nav>
        )}

        {/* ── The document ──────────────────────────────────────────────── */}
        <div ref={contentRef} className="min-w-0">
          <div className="bg-white border border-line rounded-2xl px-7 py-7 mx-auto" style={{ maxWidth: 780 }}>

            {/* Cover — only on the first section, and only once. It's the
                document's title page, not a banner on every section. */}
            {currentIndex === 0 && (
              <div className="relative overflow-hidden rounded-xl border border-line bg-white px-8 pt-9 pb-8 mb-7">
                <span className="block w-[280px] max-w-[62%]" dangerouslySetInnerHTML={{ __html: FUSION_LOCKUP }} />
                <h2 className="font-extrabold leading-[1.05] mt-8 max-w-[62%]" style={{ color: '#5b5d54', fontSize: 40 }}>{docMeta.label}</h2>
                <div className="relative z-10 mt-8 text-[13px] leading-[2] text-[#51534A]">
                  {version ? <div><span className="text-[#2A2E24]">Version:</span> <span className="font-semibold" style={{ color: '#92D400' }}>{version}</span></div> : null}
                  <div><span className="text-[#2A2E24]">Date:</span> <span className="font-semibold" style={{ color: '#92D400' }}>{todayLabel}</span></div>
                  <div className="w-14 h-1 mt-3" style={{ background: '#92D400' }} />
                </div>
                <span aria-hidden="true" className="absolute right-0 bottom-0" style={{ width: '38%', height: '58%', background: '#DDE3D0', opacity: 0.55, clipPath: 'polygon(100% 0,100% 100%,0 100%)' }} />
              </div>
            )}

            {multi && (
              <div className="mb-5 pb-3 border-b-2 border-[#D7E3C4]">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#9BA08C]">Section {currentIndex + 1} of {sections.length}</p>
                <h2 className="text-[19px] font-bold text-fusion-forest leading-tight mt-0.5">{current?.title}</h2>
              </div>
            )}

            <div className="flex flex-col gap-1 text-[#2A2E24]" style={{ fontSize: '15.5px', lineHeight: 1.75 }}>
              {(current?.blocks || []).map(b => (
                b.type === 'signature' && !signsHere ? (
                  // A 'complete' delegate sees WHAT they're contributing to and
                  // who will sign it, but never the pad. Showing a disabled
                  // signature block would imply they'd failed to fill it in.
                  <div key={b.id} className="mt-5 pt-4 border-t border-dashed border-line">
                    <p className="text-[13px] italic text-[#3a3b33] leading-relaxed mb-3">{b.statement}</p>
                    <div className="rounded-xl border border-[#EFD9A9] bg-[#FBF0DC] px-4 py-3">
                      <p className="text-[13px] font-semibold text-[#5E3D05]">You're not signing this</p>
                      <p className="text-[12px] text-[#5E3D05] mt-0.5 leading-relaxed">
                        Once you've finished, it goes back to {delegated?.invitedBy || 'the person who asked you'} to sign.
                        Your answers are saved with your name against them.
                      </p>
                    </div>
                  </div>
                ) : b.type === 'signature' ? (
                  <div key={b.id} className="mt-5 pt-4 border-t border-dashed border-line">
                    <p className="text-[13px] italic text-[#3a3b33] leading-relaxed mb-3">{b.statement}</p>
                    {(b.extras || []).filter(e => String(e.label || '').trim()).length > 0 && (
                      <div className="mb-3 flex flex-col gap-2.5">
                        {(b.extras || []).filter(e => String(e.label || '').trim()).map(e => (
                          <div key={e.id}>
                            <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#51534A] mb-1.5">
                              {e.label}{e.source ? <span className="text-[#9BA08C] font-normal normal-case tracking-normal"> · prefilled, editable</span> : null}
                            </label>
                            <input
                              value={e.source ? (values[e.source] ?? '') : (values[e.id] ?? '')}
                              onChange={ev => (e.source ? setFieldValue(e.source, ev.target.value) : setInputValue(e.id, ev.target.value))}
                              disabled={submitting}
                              placeholder={e.source ? undefined : e.label}
                              className="w-full bg-white border border-line rounded-lg px-3 py-2 text-[14px] text-ink focus:outline-none focus:border-[#2F6E14]" />
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="mb-3">
                      <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#51534A] mb-1.5">Signature</label>
                      <SignaturePad onChange={setSignatureImage} disabled={submitting} />
                    </div>
                    <div className="flex flex-wrap items-end gap-4">
                      <div className="flex-1 min-w-[220px]">
                        <label htmlFor="hf-signer-name" className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#51534A] mb-1.5">Full name (printed)</label>
                        <input
                          id="hf-signer-name"
                          value={signerName}
                          onChange={e => setSignerName(e.target.value)}
                          disabled={submitting}
                          placeholder="Your full name"
                          className="w-full bg-white border border-line rounded-lg px-3 py-2 text-[15px] text-ink focus:outline-none focus:border-[#2F6E14]" />
                      </div>
                      <div className="w-32">
                        <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#51534A] mb-1.5">Date</label>
                        <div className="text-[15px] text-ink px-3 py-2 bg-[#EEF1E6] rounded-lg">{todayLabel}</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <FormBlock
                    key={b.id}
                    block={b}
                    values={values}
                    ticks={ticks}
                    disabled={submitting}
                    onFieldChange={setFieldValue}
                    onInputChange={setInputValue}
                    onAttach={setAttachment}
                    uploadingId={uploadingId}
                    onTick={setTick} />
                )
              ))}
            </div>
          </div>

          {/* ── Move through the document ──────────────────────────────── */}
          <div className="mx-auto mt-4 flex items-center justify-between gap-3 flex-wrap" style={{ maxWidth: 780 }}>
            <button
              type="button"
              onClick={() => goToSection(sections[currentIndex - 1]?.id)}
              disabled={currentIndex === 0 || submitting}
              className="rounded-xl bg-white border border-line text-ink text-sm font-semibold px-4 py-2 transition-colors hover:border-[#C9CFB6] hover:bg-[#FAFBF4] disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest">
              ← Previous
            </button>

            {/* On the last section this is the signature; everywhere else it
                moves on. The Sign button is never silently dead — when
                something's outstanding it says so, in the live region below. */}
            {currentIndex === sections.length - 1 || (sigIndex >= 0 && currentIndex === sigIndex) ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                className="rounded-xl bg-[#92D400] text-[#131313] text-sm font-bold px-6 py-2.5 inline-flex items-center gap-2 transition-colors hover:bg-[#a4e810] disabled:opacity-45 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest">
                {submitting
                  ? (<><Spinner /><span>{signsHere ? 'Submitting…' : 'Sending…'}</span></>)
                  : <span>{signsHere ? 'Sign & submit' : 'Send back for signing'}</span>}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => goToSection(sections[currentIndex + 1]?.id)}
                disabled={submitting}
                className="rounded-xl bg-fusion-forest text-white text-sm font-bold px-6 py-2.5 transition-colors hover:bg-[#00593F] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest">
                Next section →
              </button>
            )}
          </div>

          {/* What's outstanding, always stated. aria-live so it's announced the
              moment the last clause is ticked or the last section opened. */}
          <div className="mx-auto mt-2.5" style={{ maxWidth: 780 }}>
            <p role="status" aria-live="polite" className="text-[12px] text-[#51534A]">
              {state === 'error' && errorMessage
                ? <span className="text-[#B01F06] font-semibold">Couldn't submit — {errorMessage}</span>
                : blockingMessage
                  ? <>Before you can sign: <span className="font-semibold text-[#7A4E10]">{blockingMessage}</span>.</>
                  : <span className="text-fusion-forest font-semibold">Everything's done — ready to sign.</span>}
            </p>
            {outstanding.untickedSections.length > 0 && (
              <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                {outstanding.untickedSections.map(r => (
                  <li key={r.section.id}>
                    <button
                      type="button"
                      onClick={() => goToSection(r.section.id)}
                      className="text-[12px] text-[#7A1B05] underline decoration-[#E0290A]/40 underline-offset-2 hover:decoration-[#E0290A] rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E0290A]">
                      {r.section.title} — {r.outstanding} to agree
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
